import axios from "@utils/axios-instance";
import { TUser } from "@/models/user.model";
import {
  TCreateLeadPayload,
  TLead,
  TLeadCsvJob,
  TLeadResponse,
  TLeadsFilters,
  TLeadStatus,
  TLeadStatusChanges,
} from "@/models/leads.model";
import { TLeadMergeRequest, TLeadMergeResponse } from "@/models/lead-merge.model";
import { TCreateLeadTaskPayload, TLeadTask, TUpdateTaskPayload } from "@/models/tasks.model";

// *** CRUD STARTS *** //

const createLead = async (data: TCreateLeadPayload): Promise<TLead> => {
  const response = await axios.post(`/lead/`, data);
  return response.data;
};

const getAll = async (filters: TLeadsFilters): Promise<TLeadResponse> => {
  filters = Object.fromEntries(Object.entries(filters).filter(([, val]) => Boolean(val)));
  const queryString = new URLSearchParams(filters as Record<string, string>).toString();
  const response = await axios.get(`/lead?${queryString}`);
  return response.data;
};

const getById = async (leadId: number): Promise<TLead> => {
  const response = await axios.get(`/lead/${leadId}`);
  return response.data;
};

const getByEmail = async (email: string): Promise<TLead> => {
  const response = await axios.get(`/lead/search?email=${encodeURIComponent(email)}`);
  return response.data;
};


const update = async (leadId: number, data: Partial<Omit<TLead, "id">>): Promise<TUser> => {
  const response = await axios.patch(`/lead/${leadId}`, data);
  return response.data;
};

export const deleteLead = async (leadId: number): Promise<void> => {
  await axios.delete(`/lead/${leadId}`);
};

const mergeLeads = async (payload: TLeadMergeRequest): Promise<TLeadMergeResponse> => {
  const response = await axios.post("/lead/merge", payload);
  return response.data;
};

// *** CRUD ENDS *** //

export const order = async (order: number[]): Promise<void> => {
  await axios.patch(`/lead/order`, order);
};

// Deprecated synchronous export (capped at 1000 rows) — still used by the
// questionnaire results download. Lead exports go through the job queue below.
export const exportLeads = async (): Promise<string> => {
  const response = await axios.get(`/lead/export`);
  return response.data;
};

// LEAD CSV EXPORT (queued jobs)

// The backend answers 409 when an export is already in progress for the company.
export const startLeadExport = async (): Promise<{ message: string; jobId: number }> => {
  const response = await axios.post(`/lead/export`, {});
  return response.data;
};

export const getLeadCsvJobs = async (): Promise<TLeadCsvJob[]> => {
  const response = await axios.get(`/lead/jobs`);
  return response.data;
};

export const downloadLeadCsvJob = async (jobId: number): Promise<string> => {
  const response = await axios.get(`/lead/jobs/${jobId}/download`);
  return response.data;
};

// LEAD TASKS
export const getLeadTasks = async (leadId: number): Promise<TLeadTask[]> => {
  const response = await axios.get(`/lead/${leadId}/tasks`);
  return response.data.data;
};

// One page of a lead's tasks (newest first) plus the total, for feeds that
// load more on demand.
export const getLeadTasksPage = async (
  leadId: number,
  page: number,
  pageSize: number
): Promise<{ data: TLeadTask[]; totalCount: number }> => {
  const response = await axios.get(`/lead/${leadId}/tasks`, { params: { page, pageSize } });
  return { data: response.data.data, totalCount: response.data.meta?.totalCount ?? response.data.data.length };
};

// The lead tasks endpoint pages at 5 by default (100 max). Feeds that show
// every task walk all pages at the max size.
export const getAllLeadTasks = async (leadId: number): Promise<TLeadTask[]> => {
  const all: TLeadTask[] = [];
  for (let page = 1; page <= 100; page++) {
    const response = await axios.get(`/lead/${leadId}/tasks`, { params: { page, pageSize: 100 } });
    all.push(...response.data.data);
    if (page >= (response.data.meta?.totalPages ?? 1)) break;
  }
  return all;
};

export const saveLeadTask = async (leadId: number, task: Pick<TLeadTask, "task" | "isComplete">): Promise<TLeadTask & { id: number }> => {
  const response = await axios.post(`/lead/${leadId}/tasks`, task);
  return response.data;
};

export const createLeadTask = async (leadId: number, data: TCreateLeadTaskPayload): Promise<TLeadTask> => {
  const assignedToId = data.assignedToId ? Number(data.assignedToId) : undefined;
  const response = await axios.post(`/lead/${leadId}/tasks`, { ...data, isComplete: false, assignedToId });
  return response.data;
};

export const updateLeadTaskFull = async ({
  leadId,
  taskId,
  data,
}: { leadId: number; taskId: number; data: TUpdateTaskPayload }): Promise<TLeadTask> => {
  const response = await axios.patch(`/lead/${leadId}/tasks/${taskId}`, data);
  return response.data;
};

export const removeLeadTask = async ({ leadId, taskId }: { leadId: number; taskId: number }): Promise<void> => {
  await axios.delete(`/lead/${leadId}/tasks/${taskId}`);
};

export const toggleLeadTaskStatus = async ({
  leadId,
  taskId,
  isComplete,
}: {
  leadId: number;
  taskId: number;
  isComplete: boolean;
}): Promise<void> => {
  await axios.patch(`/lead/${leadId}/tasks/${taskId}`, { isComplete });
};

export const updateLeadTask = async ({
  leadId,
  taskId,
  task,
}: {
  leadId: number;
  taskId: number;
  task: string;
}): Promise<void> => {
  await axios.patch(`/lead/${leadId}/tasks/${taskId}`, { task });
};

// LEAD STAGES
export const getLeadStatusList = async (): Promise<TLeadStatus[]> => {
  const response = await axios.get(`/lead/status`);
  return response.data;
};

export const saveLeadStatus = async (data: Pick<TLeadStatus, "name" | "color">): Promise<TLeadStatus> => {
  const response = await axios.post(`/lead/status`, data);
  // POST returns the bare LeadStatus row — only GET /lead/status computes
  // `totalLeads` — and consumers treat it as a required number. A status that
  // was just created holds no leads, so 0 is the honest fill-in.
  return { ...response.data, totalLeads: response.data.totalLeads ?? 0 };
};

/**
 * Only the fields present in `changes` are sent — `order` triggers a full
 * reorder of the catalog on the backend, where it means the target *index*
 * in the list (0 = first), so it must not be sent on plain renames.
 * `totalLeads` is derived server-side and never accepted here.
 */
export const updateLeadStatus = async (
  data: Pick<TLeadStatus, "id"> & TLeadStatusChanges
): Promise<TLeadStatus> => {
  const { id, ...changes } = data;
  const response = await axios.patch(`/lead/status/${id}`, changes);
  return response.data;
};

export const deleteLeadStatus = async (id: number, newStatusId?: number): Promise<void> => {
  // The endpoint dereferences the request body unconditionally, so omitting it
  // answers 500 even though `newStatusId` is optional. Always send an object —
  // empty when the status has no leads to reassign.
  await axios.delete(`/lead/status/${id}`, { data: newStatusId ? { newStatusId } : {} });
};

export default {
  createLead,
  getAll,
  getById,
  getByEmail,
  update,
  deleteLead,
  mergeLeads,
  order,
  getLeadStatusList,
  updateLeadStatus,
  saveLeadStatus,
  deleteLeadStatus,
  getLeadTasks,
  getAllLeadTasks,
  saveLeadTask,
  createLeadTask,
  removeLeadTask,
  toggleLeadTaskStatus,
  updateLeadTask,
  updateLeadTaskFull,
};
